import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';

const candidateByClient: Record<string, typeof initialForm> = {
  '671e5517-5274-4eb3-a7f2-6b197b3fc5f5': {
    appointmentId: '57f419ef-4fc1-4940-96e0-d0416eb35b89',
    payerPatientLinkId: '30977911-a58f-4bbb-b1f0-69f4c79632ba',
    contractId: 'a6869d60-210c-47f7-8a2d-3ee9d5cd183a',
    contractPricingRuleId: '502d5f8a-03fc-4a18-964d-bb2ba5eea0c9',
    serviceType: 'Sessão de Fisioterapia',
  },
  '88121e1e-6143-4bbd-a6b5-a0c663347e51': {
    appointmentId: 'c76366e9-3461-4003-99e8-428c3a728572',
    payerPatientLinkId: 'af5c0ec4-732b-469a-80bc-36ae52059f1b',
    contractId: '65a263af-7d91-4c89-83c6-960189108820',
    contractPricingRuleId: '046e131c-56cb-4ea4-89dc-987067cc4575',
    serviceType: 'Sessão de Fisioterapia',
  },
  'a688c204-c333-4ca3-b93d-b7a1e925ed91': {
    appointmentId: 'e740a1c6-0ae0-43e9-aa33-21990663aaac',
    payerPatientLinkId: 'e1ee0961-1f8b-4774-bbb7-13dac8f83b59',
    contractId: '39db8db9-74ed-4be8-a91a-0d94750a2d15',
    contractPricingRuleId: '2a57d9e2-ac16-4af1-b847-19439078ce6c',
    serviceType: 'Sessão de Fisioterapia',
  },
};

const initialForm = {
  appointmentId: '57f419ef-4fc1-4940-96e0-d0416eb35b89',
  payerPatientLinkId: '30977911-a58f-4bbb-b1f0-69f4c79632ba',
  contractId: 'a6869d60-210c-47f7-8a2d-3ee9d5cd183a',
  contractPricingRuleId: '502d5f8a-03fc-4a18-964d-bb2ba5eea0c9',
  serviceType: 'Sessão de Fisioterapia',
};

export default function FinancialA2RpcTestPage() {
  const { selectedClient } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [result, setResult] = useState<unknown>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const candidate = selectedClient?.clientId ? candidateByClient[selectedClient.clientId] : undefined;
    if (candidate) setForm(candidate);
  }, [selectedClient?.clientId]);

  const update = (key: keyof typeof initialForm, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const runRpc = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);
    const { data, error: rpcError } = await supabase.rpc('financial_a2_save_billable_session', {
      p_client_id: selectedClient?.clientId,
      p_appointment_id: form.appointmentId.trim(),
      p_payer_patient_link_id: form.payerPatientLinkId.trim(),
      p_contract_id: form.contractId.trim(),
      p_contract_pricing_rule_id: form.contractPricingRuleId.trim(),
      p_service_type: form.serviceType.trim(),
    });
    setLoading(false);
    if (rpcError) {
      setError(`${rpcError.message} (código ${rpcError.code ?? 'não informado'})`);
      return;
    }
    setResult(data);
  };

  return (
    <main className="min-h-screen bg-slate-100 px-4 py-8 sm:px-8">
      <section className="mx-auto max-w-4xl rounded-lg bg-white p-6 shadow-sm">
        <div className="mb-6 flex items-start justify-between gap-4 border-b pb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Acesso temporário por URL</p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">Teste da RPC A2</h1>
            <p className="mt-1 text-sm text-slate-600">Criação idempotente de item faturável de sessão.</p>
          </div>
          <Link className="text-sm text-blue-700 hover:underline" to="../admin/financial-contracts">Voltar</Link>
        </div>

        <div className="mb-5 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          Tenant selecionado: <strong>{selectedClient?.clientId ?? 'nenhum'}</strong>. A função não cria invoice nem altera o status clínico.
          {!candidateByClient[selectedClient?.clientId ?? ''] && ' Informe manualmente um candidato compatível com este tenant.'}
        </div>

        <form onSubmit={runRpc} className="grid gap-4 sm:grid-cols-2">
          {(Object.keys(form) as Array<keyof typeof initialForm>).map((key) => (
            <label key={key} className={key === 'serviceType' ? 'sm:col-span-2' : ''}>
              <span className="mb-1 block text-sm font-medium text-slate-700">{key}</span>
              <input
                required
                value={form[key]}
                onChange={(event) => update(key, event.target.value)}
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
            </label>
          ))}
          <div className="sm:col-span-2 flex items-center gap-3 border-t pt-4">
            <button disabled={loading || !selectedClient} className="rounded bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
              {loading ? 'Executando...' : 'Executar RPC'}
            </button>
            <span className="text-sm text-slate-500">Repita a operação para testar a idempotência.</span>
          </div>
        </form>

        {error && <pre className="mt-5 whitespace-pre-wrap rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</pre>}
        {result !== null && <pre className="mt-5 overflow-auto rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">{JSON.stringify(result, null, 2)}</pre>}
      </section>
    </main>
  );
}
